import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { GET_DASHBOARDS } from "./DashboardApi";
import { compareSections, sortNames } from "./DashboardUtils";
import type {
  DashboardGroupItem,
  DashboardItem,
  DashboardSection,
} from "./DashboardTypes";

export type { DashboardItem };

type DashboardNode = {
  node: DashboardItem;
};

type DashboardsQueryData = {
  dashboards: {
    edges: DashboardNode[];
  };
};

/**
 * Reduces the flat dashboards payload into one group per section. Grouping is
 * by section id, so two sections sharing a name stay apart, and a section with
 * no dashboards in the payload yields no group at all.
 */
const groupDashboardsBySection = (
  edges: DashboardNode[]
): DashboardGroupItem[] => {
  const sections = new Map<
    DashboardSection["id"],
    { section: DashboardSection; items: DashboardItem[] }
  >();

  edges.forEach(({ node }) => {
    const { section } = node;
    const menuItem: DashboardItem = { ...node };
    const group = sections.get(section.id);
    if (group) {
      group.items.push(menuItem);
    } else {
      sections.set(section.id, { section, items: [menuItem] });
    }
  });

  return [...sections.values()]
    .sort((a, b) => compareSections(a.section, b.section))
    .map(({ section, items }) => ({
      name: section.name,
      isOpen: true,
      items,
    }));
};

const useDashboardGroups = () => {
  const { data, loading, refetch } =
    useQuery<DashboardsQueryData>(GET_DASHBOARDS);

  const dashboardGroups = useMemo(() => {
    if (!data) {
      return undefined;
    }
    return sortNames(groupDashboardsBySection(data.dashboards?.edges ?? []));
  }, [data]);

  return {
    dashboardGroups,
    dashboardLoading: loading,
    refetchDashboards: refetch,
  };
};

export default useDashboardGroups;
